/**
 * Events, always in the context of the alarm that produced them.
 *
 * There used to be a log of every event in the system, and it was close to
 * unreadable: a page of "challenge missed" rows tells an operator nothing
 * without the alarm each one belongs to, and the alarm was the one thing the
 * row did not show. So the log is gone and the events live here instead —
 * under the alarm, where the question "which alarm was this?" is already
 * answered by where you are standing.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlarmClock, ListChecks, UserRound } from 'lucide-react';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  EmptyState,
  ErrorNote,
  Field,
  IdChip,
  Pagination,
  Row,
  Select,
  Skeleton,
  Spinner,
} from '../../components/ui';
import { api, compact } from '../../lib/api';
import {
  formatDateTime,
  formatDuration,
  formatNumber,
  humanize,
} from '../../lib/format';
import { translate, useT } from '../../lib/i18n';
import {
  RecordLink,
  RecordNav,
  useOnwardTrail,
  useOpenRecord,
  useTrail,
} from '../../lib/navigation';
import type { AdminAlarm, AdminAlarmEvent, Paginated } from '../../lib/types';

/** The event types worth offering as a filter. The log has many more. */
const EVENT_TYPES = [
  '',
  'rang',
  'dismissed',
  'reward_earned',
  'reward_missed',
  'reward_withheld',
  'ring_timed_out',
  'created',
  'deleted',
  'rejected',
  'integrity_failed',
];

// ── The events of one alarm ─────────────────────────────────────────────────

export function AlarmEventsTab({ alarm }: { alarm: AdminAlarm }) {
  const t = useT();
  const open = useOpenRecord();
  const onward = useOnwardTrail('alarm', alarm.id);
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);

  const events = useQuery({
    queryKey: ['alarm-events', alarm.id, type, page],
    queryFn: () =>
      api.get<Paginated<AdminAlarmEvent>>(
        '/api/events',
        compact({ alarmId: alarm.id, type, page, limit: 25 }),
      ),
  });

  if (events.isError) return <ErrorNote error={events.error} />;

  const items = events.data?.items ?? [];

  return (
    <div className="space-y-3">
      <Field label={t('events.filter.kind')}>
        <Select
          value={type}
          onChange={(event) => {
            setType(event.target.value);
            setPage(1);
          }}
        >
          {EVENT_TYPES.map((option) => (
            <option key={option} value={option}>
              {option === ''
                ? t('events.type.all')
                : t(`events.type.${option}`)}
            </option>
          ))}
        </Select>
      </Field>

      {events.isPending ? (
        <Skeleton className="h-64" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<ListChecks size={28} />}
          title={t('events.noneForAlarm')}
          description={t('events.noneForAlarmDescription')}
        />
      ) : (
        <div className="rounded-lg border border-line">
          <ul className="divide-y divide-line">
            {items.map((event) => (
              <li key={event.id}>
                <button
                  type="button"
                  onClick={() =>
                    open({ kind: 'event', id: event.id }, onward)
                  }
                  className="w-full px-3 py-2.5 text-left transition-colors hover:bg-raised"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="flex flex-wrap items-center gap-2 text-sm font-medium text-ink">
                      {humanize(event.type)}
                      {event.risk && <RiskBadge risk={event.risk} />}
                    </span>
                    <span className="tnum text-xs text-ink-3">
                      {formatDateTime(event.occurredAt)}
                    </span>
                  </div>
                  <p className="mt-0.5">
                    <EventSummary event={event} />
                  </p>
                </button>
              </li>
            ))}
          </ul>
          <Pagination
            page={events.data?.page ?? 1}
            totalPages={events.data?.totalPages ?? 1}
            total={events.data?.total ?? 0}
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
}

// ── One event ───────────────────────────────────────────────────────────────

/**
 * An event on its own, reached from its alarm or from the review queue.
 *
 * Its whole job beyond showing the record is answering "what was this attached
 * to?" — hence the two links out, to the alarm and to the account, and the
 * alarm's actual time and label rather than another opaque id.
 */
export function EventDrawer({
  eventId,
  onClose,
}: {
  eventId: string;
  onClose: () => void;
}) {
  const t = useT();
  const trail = useTrail();
  const onward = useOnwardTrail('event', eventId);

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => api.get<AdminAlarmEvent>(`/api/events/${eventId}`),
  });

  const alarmId = event.data?.alarmId ?? null;

  const alarm = useQuery({
    queryKey: ['alarm', alarmId],
    queryFn: () => api.get<AdminAlarm>(`/api/alarms/${alarmId ?? ''}`),
    enabled: alarmId !== null,
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        event.data ? humanize(event.data.type) : t('common.loadingEllipsis')
      }
    >
      {event.isPending && <Spinner />}
      {event.isError && <ErrorNote error={event.error} />}

      {event.data && (
        <div className="space-y-5">
          <RecordNav trail={trail}>
            {event.data.alarmId && (
              <RecordLink
                to={{ kind: 'alarm', id: event.data.alarmId }}
                trail={onward}
                icon={<AlarmClock size={15} />}
              >
                {t('events.viewAlarm')}
              </RecordLink>
            )}
            <RecordLink
              to={{ kind: 'user', id: event.data.userId }}
              trail={onward}
              icon={<UserRound size={15} />}
            >
              {t('events.viewUser')}
            </RecordLink>
          </RecordNav>

          {/* The alarm this belongs to, spelled out. An id is not an answer. */}
          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('events.section.belongsTo')}
            </h3>
            {event.data.alarmId === null ? (
              <p className="rounded-lg border border-line px-3 py-2.5 text-sm text-ink-3">
                {t('events.noAlarm')}
              </p>
            ) : alarm.isPending ? (
              <Skeleton className="h-14" />
            ) : (
              <div className="rounded-lg border border-line px-3 py-2.5">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="tnum text-sm font-semibold text-ink">
                    {alarm.data?.time ?? '—'}
                  </span>
                  <span className="text-sm text-ink-2">
                    {alarm.data?.label || t('alarms.noLabel')}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-ink-3">
                  {t('events.detail.alarm', {
                    id: event.data.clientAlarmId ?? '—',
                  })}
                </p>
              </div>
            )}
          </section>

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('events.section.what')}
            </h3>
            <dl className="divide-y divide-line rounded-lg border border-line px-3">
              <Row label={t('events.column.what')}>
                {humanize(event.data.type)}
              </Row>
              <Row label={t('events.filter.risk')}>
                {event.data.risk ? (
                  <RiskBadge risk={event.data.risk} />
                ) : (
                  '—'
                )}
              </Row>
              <Row label={t('events.column.credits')}>
                {event.data.amount === 0
                  ? '—'
                  : formatNumber(event.data.amount)}
              </Row>
              <Row label={t('events.row.occurred')}>
                {formatDateTime(event.data.occurredAt)}
              </Row>
              <Row label={t('events.row.received')}>
                {formatDateTime(event.data.receivedAt)}
              </Row>
            </dl>
          </section>

          <EventMetadata metadata={event.data.metadata} />

          <section>
            <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
              {t('alarms.section.identity')}
            </h3>
            <dl className="divide-y divide-line rounded-lg border border-line px-3">
              <Row label={t('events.row.eventId')} mono>
                <IdChip id={event.data.id} />
              </Row>
              <Row label={t('alarms.row.user')} mono>
                <IdChip id={event.data.userId} />
              </Row>
              <Row label={t('alarms.row.alarmId')} mono>
                {event.data.alarmId ? (
                  <IdChip id={event.data.alarmId} />
                ) : (
                  '—'
                )}
              </Row>
            </dl>
          </section>
        </div>
      )}
    </Drawer>
  );
}

/**
 * Whatever the ruling and the device had to say, rendered as it comes.
 *
 * Deliberately not curated: the summary line already picks out the fields an
 * operator scans for, and this is the place they look when the summary was not
 * enough — which is exactly when dropping an unrecognised key would hurt.
 */
function EventMetadata({
  metadata,
}: {
  metadata: Record<string, unknown> | null;
}) {
  const t = useT();
  const entries = Object.entries(metadata ?? {});
  if (entries.length === 0) return null;

  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold tracking-wide text-ink-2 uppercase">
        {t('events.section.metadata')}
      </h3>
      <dl className="divide-y divide-line rounded-lg border border-line px-3">
        {entries.map(([key, value]) => (
          <Row key={key} label={humanize(key)}>
            <span className="break-anywhere">{stringify(value)}</span>
          </Row>
        ))}
      </dl>
    </section>
  );
}

function stringify(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  // Anything shaped — a nested bag the adjudicator wrote — as it was stored.
  return JSON.stringify(value) ?? '—';
}

// ── Shared presentation ─────────────────────────────────────────────────────

/**
 * The one or two metadata keys worth showing inline.
 *
 * The metadata bag holds whatever the ruling and the device between them had
 * to say, which is too much for a list row. These are the fields an operator
 * scanning events is actually looking for.
 */
export function EventSummary({ event }: { event: AdminAlarmEvent }) {
  const meta = event.metadata ?? {};
  const parts: string[] = [];

  if (typeof meta.solveMs === 'number') {
    parts.push(
      translate('events.detail.solvedIn', {
        duration: formatDuration(meta.solveMs),
      }),
    );
  }
  if (typeof meta.durationMs === 'number' && typeof meta.solveMs !== 'number') {
    parts.push(formatDuration(meta.durationMs));
  }
  if (typeof meta.anomalies === 'string' && meta.anomalies) {
    parts.push(meta.anomalies.split(',').map(humanize).join(', '));
  }
  if (typeof meta.reason === 'string') parts.push(meta.reason);
  if (typeof meta.reviewedBy === 'string') {
    parts.push(translate('events.detail.ruledBy', { actor: meta.reviewedBy }));
  }
  if (event.clientAlarmId !== null) {
    parts.push(translate('events.detail.alarm', { id: event.clientAlarmId }));
  }

  return (
    <span className="text-xs text-ink-2 break-anywhere">
      {parts.length > 0 ? parts.join(' · ') : '—'}
    </span>
  );
}

export function RiskBadge({ risk }: { risk: string }) {
  const t = useT();
  const tone =
    risk === 'trusted' ? 'good' : risk === 'suspicious' ? 'warn' : 'danger';
  return <Badge tone={tone}>{t(`events.risk.${risk}`)}</Badge>;
}
